import { CSSProperties, ReactNode, useLayoutEffect, useRef } from 'react';

/** Each bar's slot, gap included. 40 bars span 560px. */
const BAR_WIDTH_PX = 14;

/**
 * Fades the bars out towards the visible right edge, which the chart keeps in
 * `--fade-edge`. The eased stops start the fade gently. The edge defaults to
 * past the plot, which shows no fade.
 */
const BAR_FADE: CSSProperties = {
  maskImage: `linear-gradient(to right,
    #000 calc(var(--fade-edge, 200%) - 56px),
    rgb(0 0 0 / 0.9) calc(var(--fade-edge, 200%) - 40px),
    rgb(0 0 0 / 0.65) calc(var(--fade-edge, 200%) - 24px),
    rgb(0 0 0 / 0.35) calc(var(--fade-edge, 200%) - 10px),
    rgb(0 0 0 / 0.15) var(--fade-edge, 200%))`,
};

interface ScrollingBarChartProps {
  yTitle: string;
  xTitle: string;
  ariaLabel: string;
  /** Axis labels from the top down. The first is the top of the plot. */
  yTicks: number[];
  /** Bar numbers, from 1, that get a label under the plot. */
  xTicks: number[];
  barCount: number;
  /** One element per bar. Each sets its own height. */
  children: ReactNode;
  legend?: ReactNode;
}

/**
 * A bar chart for many values. The bars are wider than a phone, so the plot
 * scrolls sideways under a fixed y-axis. The bars fade out at the visible
 * right edge to show there is more, and the fade goes once the end shows.
 */
export function ScrollingBarChart({
  yTitle,
  xTitle,
  ariaLabel,
  yTicks,
  xTicks,
  barCount,
  children,
  legend,
}: ScrollingBarChartProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Keeps `--fade-edge` on the visible right edge, in the plot's coordinates.
  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;

    const update = () => {
      const edge = scroller.scrollLeft + scroller.clientWidth;
      const atEnd = edge >= scroller.scrollWidth - 1;
      scroller.style.setProperty('--fade-edge', atEnd ? '200%' : `${edge}px`);
    };

    update();
    scroller.addEventListener('scroll', update, { passive: true });
    const resize = new ResizeObserver(update);
    resize.observe(scroller);

    return () => {
      scroller.removeEventListener('scroll', update);
      resize.disconnect();
    };
  }, []);

  return (
    <figure
      className='bg-surface-primary border-border-secondary flex flex-col gap-3
        rounded-2xl border px-5 pt-4 pb-5'
    >
      <p className='size-caption text-primary font-bold'>{yTitle}</p>
      <div className='flex gap-3'>
        <div
          aria-hidden
          className='size-caption text-secondary flex h-[203px] shrink-0
            flex-col items-end justify-between'
        >
          {yTicks.map((tick) => (
            <span key={tick} className='leading-none'>
              {tick}
            </span>
          ))}
        </div>

        <div className='min-w-0 flex-1'>
          <div
            ref={scrollRef}
            className='border-border-secondary overflow-x-auto border-l
              [scrollbar-width:none]'
          >
            <div
              className='flex flex-col gap-2.5'
              style={{ width: barCount * BAR_WIDTH_PX }}
            >
              <div className='relative h-[203px]'>
                {/* Recessive dashed grid behind the bars. */}
                <div
                  aria-hidden
                  className='border-border-secondary absolute inset-0 flex
                    flex-col justify-between border-b'
                >
                  {yTicks.slice(0, -1).map((tick) => (
                    <div
                      key={tick}
                      className='border-border-secondary border-t border-dashed'
                    />
                  ))}
                </div>
                <div
                  aria-hidden
                  className='absolute inset-0 flex justify-between'
                >
                  {[0, 1, 2, 3, 4].map((line) => (
                    <div
                      key={line}
                      className='border-border-secondary border-l border-dashed
                        first:border-l-0'
                    />
                  ))}
                </div>
                <div
                  role='img'
                  aria-label={ariaLabel}
                  className='absolute inset-0 flex items-end gap-px'
                  style={BAR_FADE}
                >
                  {children}
                </div>
              </div>
              <div aria-hidden className='relative h-4'>
                {xTicks.map((tick) => (
                  <span
                    key={tick}
                    className='size-caption text-secondary absolute
                      -translate-x-1/2'
                    style={{ left: `${((tick - 0.5) / barCount) * 100}%` }}
                  >
                    {tick}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <p className='size-caption text-primary mt-3 text-center font-bold'>
            {xTitle}
          </p>
        </div>
      </div>
      {legend}
    </figure>
  );
}
